/**
 * Import d'un export **Keeper** JSON (« Export to JSON » du coffre web,
 * `keeper export --format=json` de Keeper Commander).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Les dossiers et dossiers partages deviennent des dossiers
 * du coffre, `TFC:Keeper` / `$oneTimeCode` le code a usage unique de l'entree,
 * une carte (`bankCard`) une carte et une note chiffree (`encryptedNotes`) une
 * note ; tout autre champ devient un champ personnalise (aucune perte). Le
 * mot de passe est date de la derniere modification de la fiche
 * (`last_modified`) pour la Sante du coffre. Les fiches designees par une
 * fiche (`references` : adresse d'une carte, « Linked records ») deviennent
 * des elements lies (`entry-links.ts`).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { joinNotes, keeperCustomFields, keeperFolderPath, type ImportedEntry } from './csv-import.js';
import { normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/**
 * Valeur de champ Keeper en texte : un objet (`$name`, `$address`...) devient
 * une ligne `cle : valeur` par sous-champ renseigne, un tableau une ligne par
 * element. Rien n'est jete, rien n'arrive en `[object Object]`.
 */
function keeperFieldText(value: Json): string {
  if (Array.isArray(value)) return value.map(keeperFieldText).filter((line) => line.length > 0).join('\n');
  const object = asObject(value);
  if (object === null) return asString(value);
  return Object.entries(object)
    .map(([key, inner]) => [key, keeperFieldText(inner)] as const)
    .filter(([, text]) => text.length > 0)
    .map(([key, text]) => `${key} : ${text}`)
    .join('\n');
}

/** Instant Keeper (`last_modified`, secondes) -> ms ; absent, nul ou illisible = `null`. */
function keeperInstant(value: Json): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value * 1000) : null;
}

/** Champs d'une carte Keeper (`$paymentCard`) -> champs de carte Klavox (`typed-fields.ts`). */
const KEEPER_PAYMENT_CARD: Record<string, string> = {
  cardNumber: 'number',
  cardExpirationDate: 'expiry',
  cardSecurityCode: 'cvv',
};

/**
 * Fiches designees par une fiche Keeper -> `sourceId` des elements lies. Keeper Commander exporte chaque champ
 * `...Ref` d'une fiche (`addressRef`, `cardRef`, `recordRef`) dans `references`, cle `$<type>` ou `$<type>:<libelle>`,
 * valeur = UID ou liste d'UID (`importer/json/json.py`). `$file` designe une piece jointe, pas une fiche : ignore.
 * Jamais soi-meme, jamais deux fois.
 */
function keeperReferences(record: Record<string, Json>, uid: string): string[] {
  const references = asObject(record['references']) ?? {};
  const linked: string[] = [];
  for (const [name, value] of Object.entries(references)) {
    if (name.split(':')[0] === '$file') continue;
    for (const target of (Array.isArray(value) ? value : [value]).map(asString)) {
      if (target.length > 0 && target !== uid && !linked.includes(target)) linked.push(target);
    }
  }
  return linked;
}

/** Un export Keeper JSON se reconnait a son tableau `records` (Bitwarden porte `items`, Proton `vaults`). */
export function isKeeperExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && Array.isArray(file['records']);
}

/** Une fiche Keeper -> une entree. */
function parseKeeperRecord(record: Record<string, Json>): ImportedEntry {
  const recordType = asString(record['$type']);
  const custom = asObject(record['custom_fields']) ?? {};
  const card: Record<string, string> = {};
  const pairs: [string, string][] = [];
  for (const [name, value] of Object.entries(custom)) {
    const paymentCard = recordType === 'bankCard' && name.split(':')[0] === '$paymentCard' ? asObject(value) : null;
    if (paymentCard !== null) {
      for (const [key, inner] of Object.entries(paymentCard)) {
        const target = KEEPER_PAYMENT_CARD[key];
        if (target !== undefined) card[target] = asString(inner);
        else pairs.push([key, keeperFieldText(inner)]);
      }
      continue;
    }
    if (recordType === 'bankCard' && name === '$text:cardholderName' && card['cardholder'] === undefined) {
      card['cardholder'] = asString(value);
      continue;
    }
    if (recordType === 'bankCard' && name.split(':')[0] === '$pinCode' && card['pin'] === undefined) {
      card['pin'] = asString(value);
      continue;
    }
    pairs.push([name, keeperFieldText(value)]);
  }
  const { totp, customFields } = keeperCustomFields(pairs);
  const username = asString(record['login']);
  const password = asString(record['password']);
  const url = asString(record['login_url']);
  const isLogin = username.length > 0 || password.length > 0 || url.length > 0 || totp.secret.length > 0;
  const type: VaultItemType = recordType === 'bankCard' ? 'card' : recordType === 'encryptedNotes' || !isLogin ? 'note' : 'login';
  // Seul un identifiant porte identifiant / mot de passe / adresse : sur une carte ou une note, ils restent des champs.
  if (type !== 'login') {
    const leftovers: EntryCustomField[] = [
      { name: 'login', value: username, hidden: false },
      { name: 'password', value: password, hidden: true },
      { name: 'login_url', value: url, hidden: false },
      { name: 'oneTimeCode', value: totp.secret, hidden: true },
    ];
    customFields.unshift(...leftovers.filter((field) => field.value.length > 0));
  }
  const fields = type === 'card' ? normalizeTypedFields('card', card) : null;
  // Keeper ne date pas le mot de passe lui-meme : derniere modification de la fiche, une borne haute (un mot de
  // passe change depuis une fuite ne passe jamais pour compromis) ; aucune date sans mot de passe.
  const passwordChangedAt = type === 'login' && password.length > 0 ? keeperInstant(record['last_modified']) : null;
  const folder = asObject(Array.isArray(record['folders']) ? record['folders'][0] : null);
  // L'UID Keeper ne sert qu'a resoudre les liens a l'import : jamais scelle (`importedEntryToSecrets`).
  const uid = asString(record['uid']);
  const linkedSourceIds = keeperReferences(record, uid);
  return {
    type,
    name: asString(record['title']) || (type === 'login' ? url || username : '') || 'Sans nom',
    username: type === 'login' ? username : '',
    password: type === 'login' ? password : '',
    uris: type === 'login' && url.length > 0 ? [url] : [],
    notes: joinNotes([asString(record['notes']), totp.extraNote]),
    totpSecret: type === 'login' ? totp.secret : '',
    favorite: false,
    ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(fields !== null ? { fields } : {}),
    ...(uid.length > 0 ? { sourceId: uid } : {}),
    ...(linkedSourceIds.length > 0 ? { linkedSourceIds } : {}),
    ...keeperFolderPath(asString(folder?.['folder']), asString(folder?.['shared_folder'])),
  };
}

/**
 * Parse un export Keeper JSON. Une fiche rangee dans plusieurs dossiers
 * (Keeper le permet) est importee dans le premier.
 */
export function parseKeeperExport(data: Json): ImportedEntry[] {
  const records = asObject(data)?.['records'];
  if (!Array.isArray(records)) throw new Error('export Keeper : structure inattendue (aucune fiche)');
  return records.map(asObject).filter((record): record is Record<string, Json> => record !== null).map(parseKeeperRecord);
}
